import React, { useContext, useEffect, useState, useCallback, useImperativeHandle } from 'react';
import { FormattedMessage } from 'react-intl';
import { List, Grid, Button, Box, CircularProgress } from '@material-ui/core';
import { Panel } from '../../../../../../../Components';
import { LawQuestionGroup, CustomerQuestions } from '../../../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Group/Checklist.Group';
import { LawQuestion } from '../../../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Question/Checklist.Question';
import { CustomerQuestionForm } from '../../../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Form';
import { AptorApi, useAptorApi } from '../../../../../../../Api';
import { PropertyError } from '../../../../../../../Api/AptorApi';
import { useApiValidationSnackbar } from '../../../../../../../Api/useValidationSnackbar';
import { useList } from '../../../../../../../Hooks/useList';
import { useChecklist } from '../../../../../../../Hooks/useChecklist';
import { useStyles } from '../../../../../LawPortal.styles';
import { UserContext } from '../../../../../../../Context/UserContext/UserContext';

interface IChecklist {
  law: string;
  items: Array<IQuestion | IGroup>;
  companyUnitQuestions: ICustomerQuestion[];
}

interface ICustomerQuestion {
  id?: number;
  name: string;
}

interface IQuestion {
  id: number;
  name: string;
  type: 'question';
  hidden: boolean;
}

interface IGroup {
  id: number;
  name: string;
  type: 'group';
  groupItems: IQuestion[];
}

export interface ChecklistEditorHandle {
  getSaveData: () => { hiddenQuestions: number[]; companyUnitQuestions: ICustomerQuestion[] } | null;
}

interface ChecklistEditorProps {
  lawId: number;
  unitId: number;
  onComplete?: () => void;
  onSavingChange?: (saving: boolean) => void;
}

export const ChecklistEditor = React.forwardRef<ChecklistEditorHandle, ChecklistEditorProps>(
  ({ lawId, unitId, onComplete, onSavingChange }, ref) => {
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const { companyUnits, customerName } = useContext(UserContext);
  const [law, setLaw] = useState<string>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const classes = useStyles();

  const [
    companyUnitQuestions,
    loadCompanyUnitQuestionsQuestions,
    addCustomerQuestion,
    ,
    deleteCustomerQuestion,
  ] = useList<ICustomerQuestion>();
  const [questions, loadQuestions, updateQuestion] = useChecklist<IQuestion, IGroup>();

  useImperativeHandle(ref, () => ({
    getSaveData: () => {
      const hiddenQuestions = questions.filter((i) => i.type === 'question' && i.hidden).map((i) => i.id);
      const hiddenGroupQuestions = questions
        .filter((i) => i.type === 'group')
        .map((i) => (i as IGroup).groupItems)
        .reduce((a, b) => a.concat(b), [])
        .filter((i) => i.hidden)
        .map((i) => i.id);

      return {
        hiddenQuestions: hiddenQuestions.concat(hiddenGroupQuestions),
        companyUnitQuestions: companyUnitQuestions,
      };
    },
  }), [questions, companyUnitQuestions]);

  useEffect(() => {
    api.getCustomerLawChecklist<IChecklist>(lawId, unitId).then((checklist) => {
      if (abortController.current.signal.aborted) {
        return;
      }
      setLaw(checklist.law);
      loadQuestions(checklist.items);
      loadCompanyUnitQuestionsQuestions(checklist.companyUnitQuestions);
      setLoaded(true);
    });
  }, [api, abortController, lawId, unitId, loadCompanyUnitQuestionsQuestions, loadQuestions]);


  const onToggleHidden = useCallback(
    (questionId: number, groupId: number | undefined, hidden: boolean) => {
      updateQuestion(questionId, groupId, { hidden });
    },
    [updateQuestion],
  );

  if (!loaded) {
    return <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Panel titleKey="admin.law-portal.law.checklist.heading" titleValues={{ name: law }} style={{background:"rgb(253, 242, 243)"}}>
        <List className={classes.root}>
          {questions.map((item: any, index) =>
            item.type.toLowerCase() === 'question' ? (
              <LawQuestion key={index} {...item} index={index} toggleHidden={onToggleHidden} />
            ) : (
              <LawQuestionGroup key={index} {...item} toggleHidden={onToggleHidden} />
            ),
          )}
          {companyUnitQuestions.length > 0 && (
            <CustomerQuestions
              key="customer-questions"
              questions={companyUnitQuestions}
              loadCompanyUnitQuestionsQuestions={loadCompanyUnitQuestionsQuestions}
              remove={deleteCustomerQuestion}
              companyUnitName={companyUnits.find((unit) => unit.id === unitId)?.name ?? customerName ?? ''}
            />
          )}
        </List>
        <CustomerQuestionForm add={(name) => addCustomerQuestion({ name })} />
      </Panel>
    </Box>
  );
});

ChecklistEditor.displayName = 'ChecklistEditor';